import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Lock, CreditCard } from "lucide-react";

export default function Checkout() {
  const { items, subtotal } = useCart();
  const { user } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [loading, setLoading] = useState(false);
  const [method, setMethod] = useState("stripe");
  const [cfg, setCfg] = useState({ stripe: true, razorpay: { enabled: false }, paystack: { enabled: false } });
  const coupon = loc.state?.coupon || null;

  useEffect(() => { if (items.length === 0) nav("/cart"); }, [items.length, nav]);
  useEffect(() => { api.get("/config/payments").then((r) => setCfg(r.data)); }, []);

  const payload = () => ({
    items: items.map((it) => ({ product_id: it.product_id, quantity: it.quantity })),
    origin_url: window.location.origin,
    coupon_code: coupon,
  });

  const payStripe = async () => {
    setLoading(true);
    try { const { data } = await api.post("/checkout", payload()); window.location.href = data.checkout_url; }
    catch (e) { toast.error(e.response?.data?.detail || "Checkout failed"); setLoading(false); }
  };

  const payRazorpay = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/checkout/razorpay", payload());
      const scriptId = "rzp-script";
      if (!document.getElementById(scriptId)) {
        await new Promise((res, rej) => {
          const s = document.createElement("script"); s.id = scriptId; s.src = "https://checkout.razorpay.com/v1/checkout.js"; s.onload = res; s.onerror = rej; document.body.appendChild(s);
        });
      }
      const rzp = new window.Razorpay({
        key: data.razorpay_key_id, amount: data.amount, currency: data.currency,
        name: "PLAYA", description: "Premium Digital Marketplace", order_id: data.razorpay_order_id,
        prefill: { name: user?.name || "", email: user?.email || "" },
        theme: { color: "#D4AF37" },
        handler: async (res) => {
          try {
            await api.post("/checkout/razorpay/verify", { razorpay_order_id: res.razorpay_order_id, razorpay_payment_id: res.razorpay_payment_id, razorpay_signature: res.razorpay_signature });
            nav(`/checkout/success?provider=razorpay&order_id=${data.order_id}`);
          } catch { toast.error("Payment verification failed"); }
        },
        modal: { ondismiss: () => setLoading(false) },
      });
      rzp.open();
    } catch (e) { toast.error(e.response?.data?.detail || "Razorpay init failed"); setLoading(false); }
  };

  const payPaystack = async () => {
    setLoading(true);
    try { const { data } = await api.post("/checkout/paystack", payload()); window.location.href = data.authorization_url; }
    catch (e) { toast.error(e.response?.data?.detail || "Paystack init failed"); setLoading(false); }
  };

  const submit = () => {
    if (method === "stripe") return payStripe();
    if (method === "razorpay") return payRazorpay();
    if (method === "paystack") return payPaystack();
  };

  const methods = [
    { k: "stripe", label: "Stripe", desc: "Cards · Apple/Google Pay · Global", enabled: cfg.stripe },
    { k: "razorpay", label: "Razorpay", desc: "Cards, UPI, Wallets · Global", enabled: cfg.razorpay?.enabled },
    { k: "paystack", label: "Paystack", desc: "Cards · Bank · Africa/Global", enabled: cfg.paystack?.enabled },
  ];

  return (
    <div className="container-luxe py-16 max-w-3xl">
      <h1 className="font-display text-4xl font-semibold tracking-tight mb-12">Checkout</h1>
      <div className="glass rounded-2xl p-8">
        {!user && <div className="mb-6 p-4 rounded-lg bg-[#00A86B]/10 border border-[#00A86B]/30 text-sm">Checking out as guest. <a href="/login" className="text-[#D4AF37] underline">Login</a> to save your orders.</div>}
        <h3 className="font-display text-xl mb-4">Order Summary</h3>
        {items.map((it) => (
          <div key={it.product_id} className="flex justify-between py-3 border-b border-white/5 text-sm">
            <span>{it.name} × {it.quantity}</span>
            <span className="text-[#D4AF37]">${(it.price * it.quantity).toFixed(2)}</span>
          </div>
        ))}
        <div className="flex justify-between mt-6 text-lg font-display"><span>Total</span><span className="text-[#D4AF37]">${subtotal.toFixed(2)}</span></div>

        <h3 className="font-display text-xl mt-8 mb-4">Payment Method</h3>
        <div className="space-y-3">
          {methods.map((m) => (
            <button
              key={m.k}
              data-testid={`pay-method-${m.k}`}
              onClick={() => m.enabled && setMethod(m.k)}
              disabled={!m.enabled}
              className={`w-full text-left p-4 rounded-lg border transition ${method === m.k ? "border-[#D4AF37] bg-[#D4AF37]/5" : "border-white/10 hover:border-white/25"} ${!m.enabled ? "opacity-40 cursor-not-allowed" : ""}`}
            >
              <div className="flex items-center gap-3">
                <CreditCard size={18} className={method === m.k ? "text-[#D4AF37]" : "text-gray-400"} />
                <div className="flex-1">
                  <div className="font-medium">{m.label} {!m.enabled && <span className="text-xs text-gray-500 ml-2">(not configured)</span>}</div>
                  <div className="text-xs text-gray-500 mt-1">{m.desc}</div>
                </div>
              </div>
            </button>
          ))}
        </div>

        <button data-testid="pay-now" onClick={submit} disabled={loading} className="btn-gold w-full justify-center mt-8">
          <Lock size={16} /> {loading ? "Redirecting…" : `Pay $${subtotal.toFixed(2)}`}
        </button>
        <div className="text-xs text-gray-500 mt-4 text-center tracking-widest uppercase">Secure Checkout · Encrypted end-to-end</div>
      </div>
    </div>
  );
}
